
/**
 * FormTask Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class FormTaskService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all tasks with pagination
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );
            return {
                success: true,
                data: response.data,
                meta: response.meta || {},
                message: response.message || 'Görevler getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch tasks');
        }
    }

    /**
     * Get task by ID
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch task');
        }
    }

    /**
     * Get tasks waiting for assignment
     */
    async getWaiting(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getWaiting(params),
                { maxRetries: 2 }
            );
            return {
                success: true,
                data: response.data,
                meta: response.meta || {},
                message: response.message || 'Atama bekleyen görevler getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch waiting tasks');
        }
    }

    /**
     * Get current user's tasks
     */
    async getMyTasks(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getMyTasks(params),
                { maxRetries: 2 }
            );
            return {
                success: true,
                data: response.data,
                meta: response.meta || {},
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch my tasks');
        }
    }

    async getMyWorkInbox(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getMyWorkInbox(params),
                { maxRetries: 2 }
            );
            return {
                success: true,
                data: response.data,
                page: response.page,
                pageSize: response.pageSize,
                total: response.total,
                totalPages: response.totalPages,
                message: response.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch task hub inbox');
        }
    }

    async saveSurveyDraft(taskId, data) {
        try {
            const response = await this.api.saveSurveyDraft(taskId, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to save survey draft');
        }
    }

    async submitSurveyResponse(taskId, data) {
        try {
            const response = await this.api.submitSurveyResponse(taskId, data);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to submit survey');
        }
    }

    /**
     * Create a new task
     */
    async create(data) {
        try {
            const response = await this.api.create(data);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Görev oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create task');
        }
    }

    /**
     * Assign task to user
     */
    async assign(taskId, data) {
        try {
            const response = await this.api.assign(taskId, data);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Görev atandı'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to assign task');
        }
    }

    /**
     * Complete a task
     */
    async complete(taskId, data = {}) {
        try {
            const response = await this.api.complete(taskId, data);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Görev tamamlandı'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to complete task');
        }
    }

    /**
     * Delete task
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);
            return {
                success: true,
                data: response.data,
                message: response.message || 'Görev silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete task');
        }
    }

    /**
     * Bulk delete tasks
     */
    async bulkDelete(ids) {
        const results = { success: [], failed: [] };

        for (const id of ids) {
            try {
                await this.api.delete(id);
                results.success.push(id);
            } catch (error) {
                results.failed.push({ id, error: error.message });
            }
        }

        return {
            success: results.failed.length === 0,
            data: results,
            message: `${results.success.length}/${ids.length} görev silindi`
        };
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
